import React, { useCallback, useEffect, useState } from "react";
import { apiFetch } from "@/lib/api";
import { Card } from "@/components/earnos/kit";
const btn = "rounded-xl border border-line bg-surface-3 px-4 py-2 text-sm font-semibold text-ink disabled:opacity-50";
export default function BrowserHandoff({ domains = [] }) {
  const [runtime, setRuntime] = useState(null);
  const [missions, setMissions] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [mission, setMission] = useState("");
  const [sites, setSites] = useState([]);
  const [primary, setPrimary] = useState("");
  const [links, setLinks] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const refresh = useCallback(async () => {
    try {
      const [status, work, browsers] = await Promise.all([
        apiFetch("/browser/runtime"), apiFetch("/browser/missions"), apiFetch("/browser/sessions")
      ]);
      setError("");
      setRuntime(status);
      setMissions(Array.isArray(work) ? work : []);
      const active = Array.isArray(browsers) ? browsers : [];
      setSessions(active);
      setLinks(previous => Object.fromEntries(Object.entries(previous).filter(([id]) =>
        active.some(item => item.session_id === id && item.state === "awaiting_login"))));
    } catch (e) { setError(e.message || "Browser status is unavailable."); }
  }, []);
  useEffect(() => { refresh(); }, [refresh]);
  async function act(operation) {
    if (busy) return;
    setBusy(true); setError(""); setNotice("");
    try { await operation(); await refresh(); }
    catch (e) { setError(e.message || "Browser action needs review. Refresh before trying again."); }
    finally { setBusy(false); }
  }
  async function start(event) {
    event.preventDefault();
    await act(async () => {
      await apiFetch("/browser/sessions", { method: "POST", body: {
        mission_id: mission, domains: sites, login_domain: primary, seconds: 180
      }});
    });
  }
  async function openLogin(id) {
    await act(async () => {
      const result = await apiFetch("/browser/sessions/" + encodeURIComponent(id) + "/handoff", { method: "POST" });
      const url = new URL(result.url);
      if (url.protocol !== "https:" || url.username || url.password ||
          !["", "443", "8443"].includes(url.port) ||
          !(url.hostname === "api.onkernel.com" || url.hostname.endsWith(".onkernel.com") || url.hostname.endsWith(".kernel.sh"))) {
        throw new Error("The secure browser address could not be verified.");
      }
      setLinks(previous => ({ ...previous, [id]: result.url }));
    });
  }
  async function transition(id, action) {
    setLinks(previous => { const copy = { ...previous }; delete copy[id]; return copy; });
    await act(async () => {
      await apiFetch("/browser/sessions/" + encodeURIComponent(id) + "/" + action, { method: "POST" });
      setNotice(action === "stop" ? "Browser termination verified." : "Control returned. Account access still needs verification during the requested job.");
    });
  }
  function toggle(site) {
    const next = sites.includes(site) ? sites.filter(value => value !== site) : [...sites, site];
    setSites(next);
    if (!next.includes(primary)) setPrimary(next[0] || "");
  }
  return <Card>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <h2 className="font-bold text-ink">Secure browser sign-in</h2>
      <button className={btn} disabled={busy} onClick={refresh}>Refresh browser status</button>
    </div>
    <p className="text-sm text-ink-2 mt-3">You enter your password and MFA directly in the secure browser. EARNOS does not enter credentials. Sign-in does not approve publishing, purchases, messages, or account changes.</p>
    {error && <p role="alert" className="text-sm text-danger mt-3">{error}</p>}
    {notice && <p role="status" className="text-sm text-ink-2 mt-3">{notice}</p>}
    {!runtime ? <p className="text-sm text-ink-2 mt-3">Checking browser availability…</p> :
      !runtime.handoff_ready ? <p className="text-sm text-ink-2 mt-3">{runtime.reason || "Secure browser sign-in is unavailable."}</p> :
      !missions.length ? <p className="text-sm text-ink-2 mt-3">Start a mission before opening a browser for its work.</p> :
      <form onSubmit={start} className="space-y-3 mt-4">
        <label className="block text-sm text-ink">Mission
          <select value={mission} onChange={e => setMission(e.target.value)} required className="block w-full rounded-xl bg-surface-3 border border-line p-3 mt-1">
            <option value="">Choose an active mission</option>
            {missions.map(item => <option key={item.mission_id} value={item.mission_id}>{item.label}</option>)}
          </select>
        </label>
        <fieldset className="space-y-2"><legend className="text-sm font-semibold text-ink mb-2">Websites permitted for this session</legend>
          {!domains.length && <p className="text-sm text-ink-2">Add allowed websites below first.</p>}
          {domains.map(item => <label key={item.domain} className="flex items-center gap-2 text-sm text-ink break-all">
            <input type="checkbox" checked={sites.includes(item.domain)} onChange={() => toggle(item.domain)} />{item.domain}
          </label>)}
        </fieldset>
        <label className="block text-sm text-ink">Sign-in website
          <select value={primary} onChange={e => setPrimary(e.target.value)} required className="block w-full rounded-xl bg-surface-3 border border-line p-3 mt-1">
            <option value="">Choose a permitted website</option>
            {sites.map(site => <option key={site} value={site}>{site}</option>)}
          </select>
        </label>
        <p className="text-xs text-ink-2">Uses your configured browser allowance. This session expires after three minutes. Include a required sign-in provider only if you authorize that website too.</p>
        <button type="submit" className={btn} disabled={busy || !mission || !sites.length || !primary}>Create secure sign-in session</button>
      </form>}
    <div className="space-y-3 mt-4">{sessions.map(item => <div key={item.session_id} className="rounded-xl border border-line p-3 space-y-3">
      <p className="text-sm font-semibold text-ink">{missions.find(m => m.mission_id === item.mission_id)?.label || "Saved mission browser"}</p>
      <p className="text-sm text-ink-2">{item.state === "awaiting_login" ? "Waiting for your sign-in" : item.state === "ready" ? "Control returned; execution verification pending" : "Browser recovery requires attention"}</p>
      <p className="text-xs text-ink-2">Expires: {new Date(item.expires_at).toLocaleString()}</p>
      {item.state === "awaiting_login" && <div className="flex flex-wrap gap-2">
        {links[item.session_id] ? <a href={links[item.session_id]} target="_blank" rel="noopener noreferrer" referrerPolicy="no-referrer" className={btn}>Continue in secure browser</a> :
          <button className={btn} disabled={busy} onClick={() => openLogin(item.session_id)}>Prepare secure sign-in</button>}
        <button className={btn} disabled={busy} onClick={() => transition(item.session_id, "continue")}>Finished signing in</button>
      </div>}
      <button className={btn} disabled={busy} onClick={() => transition(item.session_id, "stop")}>Stop and disconnect browser</button>
    </div>)}</div>
    {runtime && !runtime.execution_ready && <p className="text-xs text-ink-2 mt-4">Customer browser action execution still requires acceptance verification before it is enabled.</p>}
  </Card>;
}
